import React from "react";

import ReactDom from "react-dom";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import StoreSelector from "./StoreSelector";
import FormattedAddress from "../../addresses/FormattedAddress";
import { connect } from "shared/src/stores/storeConnect";
import { areDaysSuccessive, getI18NKeyForDay } from "../../../../utils/DateUtil";
import { getContactDetails } from "./StoreInformationUtil";
import { StoreType } from "../../../../datatypes/Store";
import { FsdAddressType } from "shared/src/types/customer/FsdAddress";
import { DayName } from "../../../../utils/DateUtil";
import SitecoreStoreInformationStore, {
    State as SitecoreStoreInformationState,
    StoreOpeningHours
} from "../../../../stores/SitecoreStoreInformationStore";
import MetroStoresStore from "../../../../stores/MetroStoresStore";
import { getOnlineVisibleStores } from "../../../../utils/getOnlineVisibleStores";

type Props = {
    store: StoreType | undefined | null;
    stores: StoreType[];
    deliveryAddresses: Array<FsdAddressType>;
    onStoreSelect: () => void;
    hideTitle: boolean;
    renderInPopover: boolean;
    inMobileMenu: boolean;
    storeInformation: SitecoreStoreInformationState;
};

class StoreInfoAndSelection extends React.Component<Props> {
    storeSelector: React.Component<any, any> | null | undefined;

    componentDidMount() {
        window.addEventListener("resize", this.handleWindowResize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleWindowResize);
    }

    handleWindowResize = () => {
        // if the window resizes while the store selector is open, make sure to scroll it into view
        // this should fix problems where the mobile keyboard pops up and hides the store selection (#BT-6849)
        if (this.storeSelector) {
            const elem = ReactDom.findDOMNode(this.storeSelector);
            if (elem && "scrollIntoView" in elem && typeof elem.scrollIntoView === "function") {
                elem.scrollIntoView();
            }
        }
    };

    getDisplayOpeningHoursData(openingHours: StoreOpeningHours) {
        const prefix = openingHours.openFrom;
        let middle = " - ";
        const suffix = openingHours.openTo;

        if (openingHours.closedFrom && openingHours.closedTo) {
            middle = " - " + openingHours.closedFrom + ", " + openingHours.closedTo + " - ";
        }

        const displayString = prefix + middle + suffix;

        return {
            day: openingHours.day,
            displayString
        };
    }

    renderOpeningTime(
        daysWithSameOpeningTime: Array<{
            day: DayName;
            displayString: string;
        }>,
        key: number
    ) {
        if (daysWithSameOpeningTime.length === 1) {
            return (
                <tr key={key}>
                    <td>
                        <FormattedMessage id={getI18NKeyForDay(daysWithSameOpeningTime[0]!.day)} />
                    </td>
                    <td>{daysWithSameOpeningTime[0]!.displayString}</td>
                </tr>
            );
        }

        return (
            <tr key={key}>
                <td>
                    <FormattedMessage id={getI18NKeyForDay(daysWithSameOpeningTime[0]!.day)} />
                    {" - "}
                    <FormattedMessage id={getI18NKeyForDay(daysWithSameOpeningTime.at(-1)!.day)} />
                </td>
                <td>{daysWithSameOpeningTime[0]!.displayString}</td>
            </tr>
        );
    }

    renderOpeningTimes() {
        const storeId = this.props.store?.storeId?.locationNumber ?? undefined;
        const sitecoreData = this.props.storeInformation?.sitecoreData ?? undefined;

        if (
            !storeId ||
            !sitecoreData ||
            !sitecoreData.has(storeId) ||
            !sitecoreData.get(storeId).openingHours ||
            sitecoreData.get(storeId).openingHours.length === 0
        ) {
            return null;
        }

        return (
            <table className="table-text opening-hours margin-grid-top-xs">
                <tbody>
                    <tr>
                        <td>
                            <strong>
                                <FormattedMessage id="EX_OPENING_HOURS" />
                            </strong>
                        </td>
                    </tr>
                    {(sitecoreData.get(storeId).openingHours ?? [])
                        .map((openingTime) => this.getDisplayOpeningHoursData(openingTime))
                        .reduce((result, value) => {
                            const previousRange = result[result.length - 1];
                            // start of reduction
                            if (!previousRange) {
                                return [...result, [value]];
                            }

                            // the current day follows the previous entry, and we have the same opening hours
                            if (
                                previousRange[previousRange.length - 1].displayString === value.displayString &&
                                areDaysSuccessive(previousRange[previousRange.length - 1].day, value.day)
                            ) {
                                previousRange.push(value);
                            } else {
                                // otherwise create a new entry
                                return [...result, [value]];
                            }

                            return result;
                        }, [] as Array<Array<{ day: DayName; displayString: string }>>)
                        .map((openingTime, index) => this.renderOpeningTime(openingTime, index))}
                </tbody>
            </table>
        );
    }

    renderStoreInformationLink() {
        const storeId = this.props.store?.storeId?.locationNumber ?? undefined;
        const sitecoreData = this.props.storeInformation?.sitecoreData ?? undefined;

        if (!storeId || !sitecoreData || !sitecoreData.has(storeId) || !sitecoreData.get(storeId).storeInformationUrl) {
            return null;
        }

        return (
            <a className="test-ex-storeinformation-link" href={sitecoreData.get(storeId).storeInformationUrl}>
                <FormattedMessage id="EX_STORE_INFORMATION_LINK" />
            </a>
        );
    }

    getContactDetails() {
        const storeId = this.props.store?.storeId?.locationNumber ?? undefined;
        if (!storeId) {
            return null;
        }
        const sitecoreData = this.props.storeInformation?.sitecoreData ?? undefined;

        const defaultContactInformation = {
            phone: this.props.store?.contactInformation?.phoneNumber ?? "",
            email: this.props.store?.contactInformation?.emailAddress ?? ""
        };

        return getContactDetails(storeId, defaultContactInformation, this.props.deliveryAddresses, sitecoreData);
    }

    render() {
        const store = this.props.store;
        const storeName = store?.storeDisplayName ?? "";
        const storeAddress = store?.addressInformation ?? undefined;
        const address = {
            city: storeAddress?.city,
            street: storeAddress?.street,
            zipcode: storeAddress?.postalCode,
            houseNumber: storeAddress?.houseNumber,
            buildingName: storeAddress?.building
        };
        const contactDetails = this.getContactDetails();
        return (
            <div>
                {store ? (
                    <div>
                        {!this.props.hideTitle && (
                            <h3>
                                <FormattedMessage id="EX_GLOBAL_HEADER_SELECTED_STORE" />
                            </h3>
                        )}
                        <div className="margin-grid-bottom">
                            <Row>
                                <Col xs={12} sm={4}>
                                    <strong className="brandbar-popover-store-storename">{storeName}</strong>
                                    <div className="brandbar-popover-store-address">
                                        <FormattedAddress address={address} />
                                        {contactDetails && (
                                            <p className="brandbar-popover-store-contact">
                                                {contactDetails.phone && (
                                                    <span>
                                                        {contactDetails.phone}
                                                        <br />
                                                    </span>
                                                )}
                                                {contactDetails.email && (
                                                    <a href={"mailto:" + contactDetails.email}>
                                                        {contactDetails.email}
                                                    </a>
                                                )}
                                            </p>
                                        )}
                                        {this.renderStoreInformationLink()}
                                    </div>
                                </Col>
                                <Col xs={12} sm={8}>
                                    {this.renderOpeningTimes()}
                                </Col>
                            </Row>
                        </div>
                    </div>
                ) : null}
                <StoreSelector
                    ref={(elem) => (this.storeSelector = elem)}
                    {...this.props}
                    onlineVisibleStore={getOnlineVisibleStores()}
                />
            </div>
        );
    }
}

export default connect(
    {
        stores: [
            [
                MetroStoresStore,
                (metroStores: typeof MetroStoresStore) => ({
                    stores: metroStores.getStores()
                })
            ],

            [
                SitecoreStoreInformationStore,
                (store: typeof SitecoreStoreInformationStore) => ({
                    storeInformation: store.getState()
                })
            ]
        ]
    },
    StoreInfoAndSelection
);
